import type { WorkBook } from 'xlsx';

import { findMergedHeader, stackedCell } from './stackedHeaders';
import { collectSheetAnalyses } from './workbookSheets';

export interface CatalogSheetRow {
  fileName: string;
  sheetName: string;
  sheetRow: number;
  headerRow: unknown[];
  row: unknown[];
  cell: (header: string) => string;
}

export interface CatalogWorklistBuckets<T> {
  rowsScanned: number;
  flagged: T[];
}

export function emptyCatalogWorklist<T>(): CatalogWorklistBuckets<T> {
  return { rowsScanned: 0, flagged: [] };
}

export function mergeCatalogWorklists<T>(
  parts: readonly CatalogWorklistBuckets<T>[],
): CatalogWorklistBuckets<T> | null {
  if (parts.length === 0) return null;
  return {
    rowsScanned: parts.reduce((total, part) => total + part.rowsScanned, 0),
    flagged: parts.flatMap((part) => part.flagged),
  };
}

function scanCatalogSheet<T>(args: {
  rows: unknown[][];
  fileName: string;
  sheetName: string;
  requiredHeaders: readonly string[];
  productHeader: string;
  toFlagged: (input: CatalogSheetRow) => T;
}): CatalogWorklistBuckets<T> | null {
  const found = findMergedHeader(args.rows, args.requiredHeaders);
  if (!found) return null;

  const buckets = emptyCatalogWorklist<T>();
  for (let index = found.headerAt + 1; index < args.rows.length; index += 1) {
    const row = args.rows[index];
    if (!row) continue;
    const cell = stackedCell(row, found.headerRow);
    if (cell(args.productHeader) === '') continue;
    buckets.rowsScanned += 1;
    buckets.flagged.push(
      args.toFlagged({
        fileName: args.fileName,
        sheetName: args.sheetName,
        sheetRow: index + 1,
        headerRow: found.headerRow,
        row,
        cell,
      }),
    );
  }
  return buckets;
}

/**
 * Walk every tab, merge stacked headers, skip blank product rows.
 * Null when no sheet has the required headers.
 */
export function scanCatalogWorkbook<T>(args: {
  workbook: WorkBook;
  fileName: string;
  requiredHeaders: readonly string[];
  productHeader: string;
  toFlagged: (input: CatalogSheetRow) => T;
}): CatalogWorklistBuckets<T> | null {
  return mergeCatalogWorklists(
    collectSheetAnalyses(args.workbook, (rows, sheetName) =>
      scanCatalogSheet({
        rows,
        fileName: args.fileName,
        sheetName,
        requiredHeaders: args.requiredHeaders,
        productHeader: args.productHeader,
        toFlagged: args.toFlagged,
      }),
    ),
  );
}
